import QRCode from "react-qr-code";
import Image from "next/image";

import { Receipt, Article } from "@/types/receipt";

import {
  getArticleTypeDisplay,
  translate,
  formatDate,
  formatAddress,
} from "@/utils/formatters";

import styles from "./TicketsPopup.module.css";
import barcodeStyles from "./barcode.module.css";

interface TicketsPopupProps {
  receipt: Receipt;
  lang: string;
  ticketBarcodeImages: { [articleIndex: string]: string[] };
  article: Article;
  index: number;
  closeButton?: React.ReactNode;
}

export const TicketsPopup = ({
  receipt,
  lang,
  ticketBarcodeImages: tickets,
  article,
  index,
  closeButton,
}: TicketsPopupProps) => {
  const isCredit = article.type === "credit";
  const isTravelTicket = article.type === "travel_ticket";
  const isTicket = article.type === "ticket";
  const articleBarcodes = tickets[index.toString()] || [];

  return (
    <div key={"article-ticket" + index} id={"index-" + index}>
      <div className="box head">
        <h4>{getArticleTypeDisplay(article.type, lang)}</h4>
        {closeButton}
      </div>
      <div className="box">
        {article.art_name && (
          <p className={styles.articleName}>
            {article.art_name}
          </p>
        )}

        {article.art_description && (
          <p className={styles.section}>{article.art_description}</p>
        )}

        {/* Show expiration date for gift cards */}
        {isCredit && article.expired_at && (
          <div className={styles.section}>
            <h5>{translate(lang, "Utløpsdato", "Expiration date")}</h5>
            <p>
              {formatDate(
                article.expired_at,
                receipt.merchant.country_code,
                true
              )}
            </p>
          </div>
        )}

        {/* Only show other details for non-credit articles */}
        {!isCredit && (
          <>
            {/* Start/Departure Time */}
            {article.event_start_at && (
              <div className={styles.section}>
                <h5>
                  {isTravelTicket
                    ? translate(
                        lang,
                        "ESTIMERT AVGANGSTID",
                        "ESTIMATED TIME OF DEPARTURE"
                      )
                    : translate(lang, "STARTER", "STARTS AT")}
                </h5>
                <p>
                  {formatDate(
                    article.event_start_at,
                    receipt.merchant.country_code,
                    true
                  )}
                </p>
              </div>
            )}

            {/* End/Arrival Time */}
            {article.event_ending_at && (
              <div className={styles.section}>
                <h5>
                  {isTravelTicket
                    ? translate(
                        lang,
                        "ESTIMERT ANKOMSTTID",
                        "ESTIMATED TIME OF ARRIVAL"
                      )
                    : translate(lang, "SLUTTER", "ENDS AT")}
                </h5>
                <p>
                  {formatDate(
                    article.event_ending_at,
                    receipt.merchant.country_code,
                    true
                  )}
                </p>
              </div>
            )}

            {/* Location Details */}
            {(article.specifics || article.location) && (
              <div className={styles.detailsContainer}>
                <h4>{translate(lang, "Detaljer", "Details")}</h4>

                {/* First Row - Only show departure/arrival for travel tickets */}
                {isTravelTicket && (
                  <div className={styles.detailsRow}>
                    {article.location?.departure_reference && (
                      <div className={styles.detailsColumn}>
                        <h5>{translate(lang, "Avgang", "Departure")}</h5>
                        <p>{article.location.departure_reference}</p>
                      </div>
                    )}
                    {article.location?.arrival_reference && (
                      <div className={styles.detailsColumn}>
                        <h5>{translate(lang, "Ankomst", "Arrival")}</h5>
                        <p>{article.location.arrival_reference}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Second Row */}
                <div className={styles.detailsRow}>
                  {article.specifics?.entrance && (
                    <div className={styles.detailsColumn}>
                      <h5>{translate(lang, "Inngang", "Entrance")}</h5>
                      <p>{article.specifics.entrance}</p>
                    </div>
                  )}
                  {article.specifics?.row_number && (
                    <div className={styles.detailsColumn}>
                      <h5>{translate(lang, "Rad", "Row")}</h5>
                      <p>{article.specifics.row_number}</p>
                    </div>
                  )}
                </div>

                {/* Third Row */}
                {article.specifics?.seat_number && (
                  <div className={styles.seatSection}>
                    <h5>{translate(lang, "Sete", "Seat")}</h5>
                    <p>{article.specifics.seat_number}</p>
                  </div>
                )}

                {/* Address Information - Modified based on ticket type */}
                <div className={styles.addressSection}>
                  {article.location?.start_address && (
                    <div className={styles.addressBlock}>
                      <h5>
                        {isTicket
                          ? translate(lang, "Adresse", "Address")
                          : translate(lang, "Startadresse", "Start address")}
                      </h5>
                      <p>{formatAddress(article.location.start_address)}</p>
                    </div>
                  )}

                  {/* Only show end address and travel distance for travel tickets */}
                  {isTravelTicket && (
                    <>
                      {article.location?.end_address && (
                        <div className={styles.addressBlock}>
                          <h5>
                            {translate(lang, "Sluttadresse", "End address")}
                          </h5>
                          <p>{formatAddress(article.location.end_address)}</p>
                        </div>
                      )}
                      {article.location?.distance_value && (
                        <div>
                          <h5>
                            {translate(
                              lang,
                              "Reisedistanse",
                              "Travel distance"
                            )}
                          </h5>
                          <p>
                            {article.location.distance_value}{" "}
                            {article.location.distance_value_definition}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Barcode Section - Moved to bottom */}
        {article.barcodes && article.barcodes.length > 0 && (
          <div className={styles.barcodeSection}>
            <h4 className={styles.barcodeHeading}>
              {translate(lang, "Strekkoder", "Barcodes")}
            </h4>
            <div className={styles.barcodeGrid}>
              {article.barcodes?.map((barcode, barcodeIndex) => (
                <div
                  key={"article-ticket-barcode" + barcodeIndex}
                  className={styles.barcodeItem}
                >
                  <div>
                    {barcode.encoding === "qr" ? (
                      <QRCode value={barcode.value} size={140} level="H" title="Ticket QR code" />
                    ) : (
                      <Image
                        src={articleBarcodes[barcodeIndex] || ""}
                        alt="Barcode"
                        width={260}
                        height={100}
                        className={barcodeStyles.barcodeImage}
                      />
                    )}
                  </div>
                  {barcode.display_value && (
                    <p className={barcodeStyles.barcodeDisplayValue}>
                      {barcode.display_value}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
